import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Check, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';
import SiratiPlusOffer from './SiratiPlusOffer';

const TIER_LABELS = { monthly: 'Monthly', yearly: 'Yearly', lifetime: 'Lifetime' };

/* ═══ SIRATI+ PAGE (upgrade, or confirmation for current members) ═══ */
const PremiumPage = ({ onBack }) => {
  const { profile, refreshProfile } = useAuth();
  const [success, setSuccess] = useState(false);
  const [subStatus, setSubStatus] = useState(null);

  useEffect(() => {
    if (profile?.id) {
      api.getSubscriptionStatus(profile.id).then(r => setSubStatus(r.data)).catch(() => {});
    }
  }, [profile?.id]);

  const backButton = (
    <button onClick={onBack} className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-primary/10 rounded-full mb-6" aria-label="Go back" data-testid="premium-back">
      <ChevronLeft className="w-6 h-6 text-primary" aria-hidden="true" />
    </button>
  );

  if (subStatus?.is_premium) {
    return (
      <div className="max-w-2xl mx-auto p-4 lg:p-8 pb-24">
        {backButton}
        <div className="glass-card rounded-arch-xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-primary" aria-hidden="true" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-primary mb-2">You're part of Sirati+</h2>
          <p className="text-text-main mb-2">Thank you for supporting Sirati.</p>
          {subStatus.premium_tier && (
            <p className="text-sm text-text-main">Plan: <span className="font-medium">{TIER_LABELS[subStatus.premium_tier] || subStatus.premium_tier}</span></p>
          )}
          {subStatus.is_beta_member && <p className="text-sm text-primary mt-2">Founding Member</p>}
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto p-4 lg:p-8 pb-24">
        <div className="glass-card rounded-arch-xl p-8 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" aria-hidden="true" />
          </motion.div>
          <h2 className="font-heading text-2xl font-bold text-primary mb-2">Welcome to Sirati+!</h2>
          <p className="text-text-main mb-6">Thank you for being a founding member.</p>
          <button onClick={onBack} className="px-6 py-3 min-h-[44px] bg-primary text-white rounded-full font-medium" data-testid="premium-done">Continue to Sirati</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto p-4 lg:p-8 pb-24" data-testid="premium-page">
      {backButton}
      <div className="glass-card rounded-arch-xl p-6 sm:p-8">
        <SiratiPlusOffer
          userId={profile?.id}
          onPaid={async () => { await refreshProfile(); setSuccess(true); }}
          onSkip={onBack}
          skipLabel="Not now. Back to the free community."
        />
      </div>
    </div>
  );
};

export default PremiumPage;
